import React, { useState, useEffect } from "react";
import { API_URL } from "../../constant";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// X-axis labels - keep as 01-12
const monthLabels = [
  "01", "02", "03", "04", "05", "06",
  "07", "08", "09", "10", "11", "12"
];

// Chart options generator
const generateOptions = (title, data) => {
  // Calculate min and max from actual data
  const dataMax = Math.max(...data);
  const dataMin = Math.min(...data);
  
  // Add extra space above the maximum value (20% padding)
  const maxWithPadding = dataMax + (dataMax * 0.2);
  // Ensure min is at least 0 or lower with some padding
  const minWithPadding = Math.max(0, dataMin - (dataMin * 0.1));

  return {
    responsive: true,
    scales: {
      y: {
        min: minWithPadding,
        max: maxWithPadding,
        color: "#F9F9F9",
        title: {
          display: true,
          text: "TopCoins",
          color: "#fff",
        },
        grid: {
          color: "#9AADFF",
          borderDash: [5, 5],
          lineWidth: 1,
        },
        ticks: {
          callback: (value) => "" + value.toLocaleString(),
          color: "#F9F9F9",
        },
      },
      x: {
        ticks: {
          color: "#F9F9F9",
        },
        title: {
          display: true,
          text: "Time Period",
          color: "#fff",
        },
        grid: {
          color: "#9AADFF",
          borderDash: [5, 5],
          lineWidth: 1,
        },
      },
    },
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          color: "#F9F9F9",
        },
      },
      title: { 
        display: false,  
        color: "#F9F9F9", 
        text: title, 
        position: 'bottom',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `TopCoins: ${context.parsed.y.toLocaleString()}`;
          },
          title: function(context) {
            // Convert month number to month name for tooltip only
            const monthNames = [
              "January", "February", "March", "April", "May", "June",
              "July", "August", "September", "October", "November", "December"
            ];
            const monthIndex = parseInt(context[0].label) - 1;
            return monthNames[monthIndex] || `Month ${context[0].label}`;
          }
        }
      }
    },
  };
};

// Chart data generator
const generateChartData = (labels, datasets) => ({
  labels,
  datasets,
});

// Function to get current month index (0-11)
const getCurrentMonthIndex = () => {
  return new Date().getMonth();
};

// Function to process data - only next month as 0
const processChartData = (data, currentMonthIndex) => {
  // Create a copy of the data array
  const processedData = [...data];
  
  // If it's December (index 11), no next month to set as 0
  if (currentMonthIndex === 11) {
    return processedData;
  }
  
  // Only set the next month (currentMonthIndex + 1) to 0
  const nextMonthIndex = currentMonthIndex + 1;
  if (nextMonthIndex < 12) {
    processedData[nextMonthIndex] = 0;
  }
  
  return processedData;
};

const ChartCoin = () => {
  const [revenueData, setRevenueData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonthIndex, setCurrentMonthIndex] = useState(getCurrentMonthIndex());

  // Fetch chart data from backend
  const fetchChartData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/public/wallet/chart-data`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch chart data');
      }
      
      const data = await response.json();
      
      // Use the monthly_points data from backend
      setRevenueData(data.monthly_points || Array(12).fill(0));
    } catch (err) {
      console.error('Error fetching chart data:', err);
      // Fallback to zeros if API fails
      setRevenueData(Array(12).fill(0));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChartData();
    // Update current month index when component mounts
    setCurrentMonthIndex(getCurrentMonthIndex());
  }, []);

  // Process the data based on current month
  const chartData = loading 
    ? Array(12).fill(0) 
    : processChartData(revenueData, currentMonthIndex);
  
  // Get current and next month names for display
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  const currentMonthName = monthNames[currentMonthIndex];
  const nextMonthIndex = currentMonthIndex === 11 ? null : currentMonthIndex + 1;
  const nextMonthName = nextMonthIndex !== null ? monthNames[nextMonthIndex] : null;

  return (
    <>
      <div className="text-center py-4">
        <h4 className="text-theme">
          Graph showing how customers can collect TopCoin Rewards over time
        </h4>
      </div>
      <div
        className="chart-wrapper"
        style={{ backgroundColor: "#000067", padding: "20px" }}
      >
        <Line
          data={generateChartData(monthLabels, [
            {
              label: "TopCoins",
              data: chartData,
              borderColor: "rgba(206,213,255,1)",
              backgroundColor: "rgba(206,213,255,0.2)",
              tension: 0.4,
              fill: true,
              pointBackgroundColor: function(context) {
                const index = context.dataIndex;
                // Make current month point a different color
                if (index === currentMonthIndex && !loading) {
                  return "#FF6B6B";
                }
                // Make next month point (value = 0) a different color
                if (index === currentMonthIndex + 1 && !loading && currentMonthIndex !== 11) {
                  return "#4ECDC4";
                }
                return "rgba(206,213,255,1)";
              },
              pointRadius: function(context) {
                const index = context.dataIndex;
                // Make current month point larger
                if (index === currentMonthIndex && !loading) {
                  return 6;
                }
                // Make next month point smaller
                if (index === currentMonthIndex + 1 && !loading && currentMonthIndex !== 11) {
                  return 3;
                }
                return 4;
              }
            },
          ])}
          options={generateOptions("Time Period", chartData)}
        />
      </div>
    </>
  );
};

export default ChartCoin;